import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { clearDevSms, listDevSms } from "../api/endpoints/dev"
import { formatDateTime } from "../utils/formatting"

export function DevSmsPage() {
  const queryClient = useQueryClient()
  const [copied, setCopied] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ["dev-sms"],
    queryFn: listDevSms,
    refetchInterval: 3000,
  })

  const messages = query.data?.data?.messages ?? []

  async function handleClear() {
    await clearDevSms()
    queryClient.invalidateQueries({ queryKey: ["dev-sms"] })
  }

  function handleCopy(otp: string) {
    navigator.clipboard.writeText(otp)
    setCopied(otp)
    setTimeout(() => setCopied(null), 1500)
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Live testing SMS Inbox — OTP messages captured by the API will appear here while Dev SMS is enabled.
        </div>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">SMS Inbox</h1>
            <p className="text-sm text-slate-500 mt-1">
              OTPs appear here automatically while live SMS capture is enabled. Auto-refreshes every 3 seconds.
            </p>
          </div>
          {messages.length > 0 && (
            <button
              type="button"
              onClick={handleClear}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Clear all
            </button>
          )}
        </div>

        {messages.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center">
            <p className="text-sm text-slate-500">
              No OTP messages yet. Request an OTP from the login page to see it here.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Phone Number
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    OTP Code
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Sent At
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {messages.map((msg, i) => (
                  <tr key={`${msg.phoneNumber}-${msg.sentAt}-${i}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-700">{msg.phoneNumber}</td>
                    <td className="px-4 py-3">
                      <code className="rounded bg-slate-100 px-2 py-1 text-lg font-bold text-slate-900 tracking-widest">
                        {msg.otp}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {formatDateTime(msg.sentAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleCopy(msg.otp)}
                        className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                      >
                        {copied === msg.otp ? "Copied!" : "Copy"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
